import { Copy } from "lucide-react";
import { Button } from "@runcheetah/ui";

export type MaterializedPromptPanelPrompt = {
  id: string;
  component: string;
  operation: string;
  provider: string;
  model: string;
  success: boolean;
  copyText: string;
  createdAt: string;
};

export function MaterializedPromptPanel({
  prompts,
}: {
  prompts: MaterializedPromptPanelPrompt[] | undefined;
}) {
  return (
    <div className="w-full space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Materialized Prompts</h3>
          <p className="text-xs text-muted-foreground">
            Exact text and metadata captured for LLM calls in this generation attempt.
          </p>
        </div>
        <span className="rounded-full bg-muted px-2.5 py-1 font-mono text-xs text-muted-foreground">
          {prompts?.length ?? 0} prompt{prompts?.length === 1 ? "" : "s"}
        </span>
      </div>

      {prompts && prompts.length > 0 ? (
        <div className="space-y-3">
          {prompts.map((prompt, index) => (
            <div key={prompt.id} className="overflow-hidden rounded-2xl border bg-muted/20">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-background px-3 py-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-foreground">
                    <span>{index + 1}.</span>
                    <span>{prompt.component}</span>
                    <span className="text-muted-foreground">/</span>
                    <span className="font-mono">{prompt.operation}</span>
                    {!prompt.success ? (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                        failed
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 truncate font-mono text-[11px] text-muted-foreground">
                    {prompt.provider}:{prompt.model} · {new Date(prompt.createdAt).toLocaleString()}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void navigator.clipboard?.writeText(prompt.copyText)}
                >
                  <Copy className="size-3.5" />
                  Copy
                </Button>
              </div>
              <textarea
                readOnly
                value={prompt.copyText}
                onFocus={(event) => event.currentTarget.select()}
                className="min-h-56 w-full resize-y border-0 bg-transparent p-3 font-mono text-xs leading-5 text-foreground outline-none"
                aria-label={`Materialized prompt ${prompt.component} ${prompt.operation}`}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
          No materialized prompts were recorded for this attempt. Check the sampling flag or rerun
          the task after the prompt table migration is applied.
        </div>
      )}
    </div>
  );
}
